/**
 * BUGFIX (fluxo Nova Análise → Relatório) — orquestração do CTA "Analisar".
 *
 * Regras do fluxo:
 *  - um único disparo por partida (duplo clique não cria duas análises);
 *  - navegação **somente** quando o backend devolve o `analysisId` persistido;
 *  - erro mantém o usuário na busca e devolve mensagem legível.
 *
 * Sem I/O aqui: `create` e `navigate` entram por injeção, o que permite testar
 * o fluxo sem rede, sem navegador e sem duplicar a implementação do serviço.
 */

export interface AnalyzeResult {
  analysisId: string | null;
  error: string | null;
}

export interface AnalyzeFlowDeps {
  /** Chama o fluxo real de criação (Edge Function `analysis` → action `create`). */
  create: (fixtureId: string) => Promise<AnalyzeResult>;
  navigate: (path: string) => void;
}

export interface AnalyzeFlowOutcome {
  ok: boolean;
  analysisId: string | null;
  error: string | null;
}

export interface SingleFlight {
  readonly busy: boolean;
  run<T>(task: () => Promise<T>): Promise<{ skipped: boolean; value: T | null }>;
}

/** Trava de disparo único, usada pelo componente para o duplo clique. */
export function createSingleFlight(): SingleFlight {
  let inFlight = false;
  return {
    get busy() {
      return inFlight;
    },
    async run<T>(task: () => Promise<T>) {
      if (inFlight) return { skipped: true, value: null };
      inFlight = true;
      try {
        return { skipped: false, value: await task() };
      } finally {
        inFlight = false;
      }
    },
  };
}

/** Rota canônica do relatório: sempre o id da análise persistida (snapshot), nunca o da partida. */
export function reportPath(analysisId: string): string {
  return `/report/${analysisId}`;
}

export async function analyzeFixture(fixtureId: string, deps: AnalyzeFlowDeps): Promise<AnalyzeFlowOutcome> {
  const { analysisId, error } = await deps.create(fixtureId);

  if (error) {
    return { ok: false, analysisId: null, error };
  }
  if (!analysisId) {
    return { ok: false, analysisId: null, error: "A análise não retornou um identificador. Tente novamente." };
  }

  deps.navigate(reportPath(analysisId));
  return { ok: true, analysisId, error: null };
}
